import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

export const TEAMS_NOTIFICATION_SEVERITIES = [
  "info",
  "warning",
  "error",
] as const;
export type TeamsNotificationSeverity =
  (typeof TEAMS_NOTIFICATION_SEVERITIES)[number];

/**
 * Send Teams Notification Step
 *
 * Drops a notification into the recipient's Microsoft Teams activity feed
 * (the bell icon) using Graph's `sendActivityNotification`. Resolves the
 * recipient by Gently user id (looking up the email) or by email directly,
 * then by AAD object id. The notification opens the configured deep-link
 * URL when clicked.
 *
 * `activityType` must match an entry in the Gently Teams app manifest's
 * `activities.activityTypes`. The manifest's `templateText` renders the
 * message and references `{actor}` (system-supplied) plus `{title}` /
 * `{message}` template parameters this step provides.
 *
 * Title and body come from inputs by default. The `titleOverride` and
 * `bodyOverride` config fields exist for testing convenience — when set,
 * they replace the input values without changing the wiring.
 */
export const sendTeamsNotificationStep = defineStepConfig({
  type: "send_teams_notification",
  name: "Send Teams Notification",
  description:
    "Sends an activity-feed notification to a user in Microsoft Teams. Uses the org's Teams integration credentials.",
  category: "Integration",

  inputSchema: z
    .object({
      userId: z
        .string()
        .uuid()
        .optional()
        .describe(
          "Internal ERP user id. The user's email is used to resolve a Teams account. Either userId or userEmail must be provided.",
        ),
      userEmail: z
        .string()
        .optional()
        .describe(
          "Email used to resolve a Teams account directly. Either userId or userEmail must be provided.",
        ),
      title: z.string().describe("Notification title / preview text"),
      body: z.string().describe("Notification body shown in the activity feed"),
      webUrl: z
        .string()
        .optional()
        .describe(
          "Deep link the notification opens when clicked. Defaults to the workflow run URL.",
        ),
    })
    .refine((d) => Boolean(d.userId || d.userEmail), {
      message: "Either userId or userEmail must be provided",
      path: ["userId"],
    }),

  outputSchema: z.object({
    delivered: z
      .boolean()
      .describe("True when Graph accepted the notification (HTTP 204)."),
    recipientUserId: z
      .string()
      .describe("AAD object id of the recipient the notification was sent to."),
  }),

  configSchema: z.object({
    activityType: z
      .string()
      .optional()
      .describe(
        "Activity type from the Teams app manifest. Defaults to the integration's defaultActivityType.",
      ),
    severity: z
      .enum(TEAMS_NOTIFICATION_SEVERITIES)
      .optional()
      .describe(
        "Optional severity passed through to the manifest template. Manifests can use it to vary the rendered text.",
      ),
    titleOverride: z
      .string()
      .optional()
      .describe(
        "Testing convenience: when set, replaces the input title without rewiring upstream sockets.",
      ),
    bodyOverride: z
      .string()
      .optional()
      .describe(
        "Testing convenience: when set, replaces the input body without rewiring upstream sockets.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
